import {
  Controller,
  Control,
  FieldValues,
  DeepMap,
  FieldError,
  RegisterOptions,
} from 'react-hook-form';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  InputLabel,
  ListItemText,
  Select,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import { useCommonTranslation } from '../../hooks/useCommonTranslation';
import { useState } from 'react';

const BtnContainer = styled('div')(({ theme }) => ({
  position: 'sticky',
  bottom: 0,
  display: 'flex',
  flexDirection: 'row',
  flexGrow: 1,
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

interface ChipSelectMenuProps {
  label: string;
  name: string;
  values: any[];
  valueAttr: string;
  labelAttr: string;
  control: Control<any>;
  required?: boolean;
  errors?: DeepMap<FieldValues, FieldError>;
  rules?: Exclude<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs'
  >;
  defaultValue?: any;
  className?: string;
  variant?: 'outlined' | 'filled' | 'standard' | undefined;
  showOkCancel?: boolean;
}

export default function ChipSelectMenu({
  label,
  name,
  control,
  values,
  valueAttr,
  labelAttr,
  required = false,
  errors,
  rules = undefined,
  defaultValue = undefined,
  className = undefined,
  variant = 'outlined',
  showOkCancel = false,
}: ChipSelectMenuProps) {
  const t = useCommonTranslation();

  const [open, setOpen] = useState(false);
  const [originValue, setOriginValue] = useState();
  const [tempValue, setTempValue] = useState();

  const handleChange = (field: any, event: any) => {
    if (!originValue) {
      setOriginValue(field.value);
    }
    field.onChange(event.target.value);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleCancel = (field: any) => {
    field.onChange(originValue);
    handleClose();
  };

  const handleOk = (field: any) => {
    setOriginValue(field.value);
    handleClose();
  };

  const getChipLabel = (id: any): string | undefined => {
    const val = values.find((v) => v[valueAttr] === id);
    return val ? val[labelAttr] : undefined;
  };

  return (
    <Controller
      name={name as any}
      control={control}
      defaultValue={defaultValue || ''}
      rules={rules}
      render={({ field }) => (
        <FormControl className={className}>
          <InputLabel
            id="member-notes-label"
            variant={variant}
            required={required}>
            {label}
          </InputLabel>
          <Select
            {...field}
            required={required}
            labelId="member-notes-label"
            label={label}
            variant={variant}
            multiple={true}
            error={errors ? !!errors[name] : false}
            open={open}
            onClose={handleClose}
            onOpen={handleOpen}
            onChange={(evt) => handleChange(field, evt)}
            value={tempValue || field.value}
            renderValue={(selected) => {
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected?.map((val: any) => {
                    const label = getChipLabel(val);
                    return label ? (
                      <Chip color="primary" key={val} label={label} />
                    ) : null;
                  })}
                </Box>
              );
            }}>
            {values.map((val) => {
              return (
                <MenuItem value={val[valueAttr]} key={val[valueAttr]}>
                  <Checkbox
                    checked={
                      field.value?.findIndex(
                        (v: any) => v === val[valueAttr],
                      ) >= 0
                    }
                  />
                  <ListItemText primary={val[labelAttr]} />
                </MenuItem>
              );
            })}
            {showOkCancel && (
              <BtnContainer>
                <Button onClick={() => handleCancel(field)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={() => handleOk(field)}>
                  {t('common.ok')}
                </Button>
              </BtnContainer>
            )}
          </Select>
        </FormControl>
      )}
    />
  );
}
